import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams, useHistory, Prompt} from 'react-router-dom';
import env from '../../../util/env';
import styled from 'styled-components';
import {contentIcAdd24px} from '@ey/motif-react-wrapper/assets/icons';
import MotifErrorMessage from '@ey/motif-react-wrapper/ErrorMessage';
import MotifIcon from '@ey/motif-react-wrapper/Icon';
import MotifSelect from '@ey/motif-react-wrapper/Select';
import MotifOption from '@ey/motif-react-wrapper/Option';
import MotifFormField from '@ey/motif-react-wrapper/FormField';
import MotifSearch from '../../Common/Search/MotifSearch';
import {currentResource, labels} from '../../../util/utils';
import utilityfunctions from '../../../util/utilityfunctions';
import {
	serviceURL,
	manageProjectTabs,
	displayLanguageListItems,
	promiseAreas,
	ProjectStatus,
	UserType,
	UserLookupType,
	projectStatusEnum
} from '../../../util/uiconstants';
import TabBar from '../../Common/TabBar/TabBar';
import {contentIcAddCircle24px} from '@ey/motif-react-wrapper/assets/icons';
import TrackerLoader from '../../Common/LoadingIndicator/TrackerLoader';
import CommonAdminTable from '../Common/CommonAdminTable';
import ClientsTable from './ClientsTable';
import EntitiesTable from './EntitiesTable';
import EngagementsTable from './EngagementsTable';
import MembersTable from './MembersTable';
import {getProjectDetails, updateProjectStatus, cleanProjectDetails} from '../../../actions/Project/projectAction';
import {setIsUnsavedUserDataModalOpen} from '../../../actions/AdminUser/AdminUserAction';
import {setUnsavedDataModalState, clearUnsavedDataState} from '../../../actions/AdminUser/UnsavedDataAction';
import {raiseJSValidationError, clearErrors} from '../../../actions/erroractions';
import {addMember, deleteMember} from '../../../actions/AccessGroups/Addmemberactions';
import useDetailsSelector from '../Common/Hooks/GetDetailsHook';
import _ from 'lodash';
import AddMemberGroup from './AddMemberGroup';
import utility from '../../../util/utilityfunctions';
import {getProjectAccessGroups} from '../../../actions/AccessGroups/Accessgroupactions';
import {stringFormat} from '@ey/utils';
import {Buffer} from 'buffer';
import Button from '../../CoreWrappers/Button';

const ManageProjects = () => {
	//#region Variables
	const managerProjectTabs = [
		{
			id: manageProjectTabs.clients,
			label: labels.clientNameLbl
		},
		{
			id: manageProjectTabs.orgChartEntities,
			label: labels.orgChartEntities
		},
		{
			id: manageProjectTabs.connectedEngagements,
			label: labels.connectEngagements
		},
		{
			id: manageProjectTabs.projectOwners,
			label: labels.projectOwners
		}
	];

	const projectIdRegex = /[\w\d\-]+/g;
	//#endregion

	const dispatch = useDispatch();

	//#region Selectors
	const userPreferences = useSelector((store) => store.userPreferences);
	const project = useSelector((store) => store.project);
	const membersInfo = useDetailsSelector('users');
	const groupsInfo = useDetailsSelector('groups');
	//#endregion

	//#region State variables

	const [searchProjectID, setSearchProjectID] = useState('');
	const [errorMessage, setErrorMessage] = useState('');
	const [showNoResultMessage, setShowNoResultMessage] = useState(false);
	const [projectsActiveTab, setProjectsActiveTab] = useState(manageProjectTabs.clients);
	const [isProjectLoaded, setIsProjetLoaded] = useState(false);
	const [currentProject, setCurrentProject] = useState([]);
	const [showAddMemberModal, setShowAddMemberModal] = useState(false);
	const [selectedUser, setSelectedUser] = useState({});
	const [selectedGroupMembers, setSelectedGroupMembers] = useState([]);
	const [updatedOwners, setUpdatedOwners] = useState({});
	const [saveFailed, setSaveFailed] = useState(false);
	const [toastErrorMessage, setToastErrorMessage] = useState('');
	const [selectedStatus, setSelectedStatus] = useState('');
	//#endregion

	const detailsTableConfig = {
		columns: [
			{
				label: labels.projectID,
				sortable: false,
				propName: 'projectId',
				width: '33.3%'
			},
			{
				label: labels.projectName,
				sortable: false,
				propName: 'name',
				width: '33.3%'
			},
			{
				label: labels.status,
				sortable: false,
				propName: 'status',
				width: '33.3%',
				customCellRender: (item) => {
					return (
						<MotifFormField>
							<MotifSelect
								multiple={false}
								name="motif-select-role"
								id="role"
								key={item.projectId}
								value={item.status}
								disabled={
									item.status.toLowerCase() === projectStatusEnum.DRAFT ||
									item.status.toLowerCase() === projectStatusEnum.DELETE
										? true
										: false
								}
								onChange={(option) => {
									if (option && option !== '') {
										if (item.status !== option) {
											setSelectedStatus(option);
											dispatch(setUnsavedDataModalState('projectStatus', true));
										} else {
											setSelectedStatus(item.status);
											dispatch(setUnsavedDataModalState('projectStatus', false));
										}
									}
								}}
								className="manage-admin-dropdown"
								ariaLabelledBy="select-label"
								title={
									currentResource.ProjectStatusLabels.find(
										(x) => x.value.toLowerCase() === selectedStatus.toLowerCase()
									)?.label
								}
							>
								{ProjectStatus.filter(
									(item) => item.value !== projectStatusEnum.INITIAL && item.value !== projectStatusEnum.CANCELLED
								).map((status) => {
									return (
										<MotifOption className={'ellipsis'} title={status.value} key={status.id} value={status.value}>
											{currentResource.ProjectStatusLabels.find((x) => x.id === status.id)?.label}
										</MotifOption>
									);
								})}
							</MotifSelect>
						</MotifFormField>
					);
				}
			}
		],
		tableClassName: 'project-details-table',
		items: isProjectLoaded ? currentProject : [],
		sortConfig: {
			sortBy: 'name',
			sortIsAscending: true
		},
		paginationConfig: {disabled: true}
	};

	//#region Effects
	useEffect(() => {
		setIsProjetLoaded(false);
		setProjectsActiveTab(manageProjectTabs.clients);

		let projectId = utilityfunctions.getProjectIdFromUrl();
		if (Boolean(projectId)) {
			dispatch(
				getProjectDetails(projectId, '?includeEngagementsDetail=true&includeGroupDetails=true', {
					supressErrorOnUi: true,
					transform: true
				})
			).then(() => {
				setSearchProjectID(projectId);
				setIsProjetLoaded(true);
			});
		}
		return resetState();
	}, []);

	useEffect(() => {
		if (_.size(project) > 0) {
			setIsProjetLoaded(true);
			const projectStatus = project.status.toLowerCase();
			setCurrentProject([
				{
					projectId: project.projectId,
					name: project.name,
					status: projectStatus
				}
			]);
			setSelectedStatus(projectStatus);
		}
	}, [project]);

	useEffect(() => {
		if (
			userPreferences.isPreferencesLoaded &&
			(!userPreferences.userType === 'Admin' ||
				(!userPreferences.isAdminReader && !userPreferences.isAdminWriter && !userPreferences.isSuperAdmin))
		) {
			// this is to let the current resource acquire the language first and then display the message
			setTimeout(() => {
				dispatch(raiseJSValidationError(currentResource.AdminUserErrors.userError));
			}, 900);
		}
	}, [userPreferences]);

	useEffect(() => {
		if (selectedGroupMembers.length > 0) {
			dispatch(setUnsavedDataModalState('addMembers', true));
		}
	}, [selectedGroupMembers]);

	useEffect(() => {
		//dispatch(clearErrors()).then(() => {
		if (saveFailed) {
			dispatch(raiseJSValidationError(toastErrorMessage));
			setSaveFailed(false);
		}
		//});
	}, [saveFailed]);
	//#endregion

	//#region Functions

	const handlerOnSearchChange = (value) => {
		let IsInvalidstring = false;
		setErrorMessage('');
		setShowNoResultMessage(false);
		if (!value || value.length > 50 || _.trim(value).length <= 0 || !projectIdRegex.test(value)) {
			IsInvalidstring = true;
		}
		if (IsInvalidstring) {
			setSearchProjectID('');
			setErrorMessage(labels.validProjectIDError);
		} else {
			setSearchProjectID(value);
			setErrorMessage('');
		}
	};

	const handlerSearch = (searchString) => {
		if (!searchString || _.size(searchString.searchProjectID) === 0) {
			setErrorMessage(labels.validProjectIDError);
		} else {
			let apiCalls = [];
			resetState();
			setSearchProjectID(searchString.searchProjectID);
			if (searchString && _.size(searchString.searchProjectID) > 0) {
				apiCalls.push(
					Promise.resolve(
						dispatch(
							getProjectDetails(
								searchString.searchProjectID,
								'?includeEngagementsDetail=true&includeGroupDetails=true',
								{
									supressErrorOnUi: true,
									transform: true
								}
							)
						)
					)
				);
				Promise.all(apiCalls)
					.catch(() => {
						dispatch(clearErrors());
						setShowNoResultMessage(true);
					})
					.finally();
			}
		}
	};

	const handlerClearSearch = () => {
		if (!_.isEmpty(searchProjectID)) {
			resetState();
		}
	};

	const resetState = () => {
		dispatch(cleanProjectDetails());
		setSearchProjectID('');
		setErrorMessage('');
		setShowNoResultMessage(false);
		setProjectsActiveTab(manageProjectTabs.clients);
		setIsProjetLoaded(false);
		setCurrentProject([]);
		setSelectedStatus('');
		dispatch(setIsUnsavedUserDataModalOpen(false));
	};

	const renderSwitch = (activeTab) => {
		switch (activeTab) {
			case manageProjectTabs.clients: {
				return <ClientsTable />;
			}
			case manageProjectTabs.orgChartEntities: {
				return <EntitiesTable />;
			}
			case manageProjectTabs.connectedEngagements: {
				return <EngagementsTable />;
			}
			case manageProjectTabs.projectOwners: {
				return (
					<MembersTable
						currentOwners={updatedOwners}
						updateOwners={setUpdatedOwners}
						addedMembers={getAddedMembers()}
						currentProject={currentProject}
					/>
				);
			}
			default:
				return <></>;
		}
	};

	const addMembers = (users, groupId, promises, errors) => {
		let usersPayload = [];
		_.forEach(users, (user) => {
			usersPayload.push({
				userId: user.userId,
				firstName: user.firstName,
				lastName: user.lastName,
				emailAddress: user.emailAddress,
				isInternal: user.userType === 'Member' ? true : false
			});
		});

		const payloadData = usersPayload;
		const url = `${env.getBaseURL(serviceURL)}Projects/${currentProject[0].projectId}/Groups/${groupId}/Users`;
		promises.push(
			dispatch(addMember(url, payloadData)).catch((error) => {
				errors.push(error);
			})
		);
	};

	const save = () => {
		let promises = [];
		let errors = [];
		let usersToRemove = [];
		let usersToAdd = [];
		if (_.size(currentProject) > 0) {
			const projectId = currentProject[0].projectId;
			//save for add member
			saveAddedMembers();

			//Api call for status change
			const status = selectedStatus.toLowerCase();
			if (currentProject[0].status.toLowerCase() !== status) {
				const projectApiUrl = `${env.getBaseURL(serviceURL)}Admins/Projects/${projectId}/ProjectStatus`;
				const payload = {
					id: projectId,
					body: {status: status}
				};
				promises.push(dispatch(updateProjectStatus(projectApiUrl, payload)));
			}

			//Api call for add/remove project owners
			let groupId = _.find(groupsInfo, (g) => {
				if (g.groupName == 'Project owners') return true;
			})?.accessGroupId;
			if (_.size(updatedOwners) > 0) {
				for (let id in updatedOwners) {
					if (updatedOwners[id]) {
						let userInfo = _.find(membersInfo, (user) => {
							if (user.userId === id) return true;
						});
						usersToAdd.push(userInfo);
					} else {
						usersToRemove.push(id);
					}
				}
			}

			if (_.size(usersToAdd) > 0) {
				addMembers(usersToAdd, groupId, promises, errors);
			}

			if (_.size(usersToRemove) > 0) {
				_.forEach(usersToRemove, (userId) => {
					let buff = new Buffer(userId);
					let base64UserId = buff.toString('base64');
					const url = `${env.getBaseURL(serviceURL)}Projects/${projectId}/Groups/${groupId}/Users/${base64UserId}`;
					promises.push(dispatch(deleteMember(url)));
				});
			}

			Promise.all(promises).then(() => {
				let errorMessages = '';
				if (_.size(errors) > 0) {
					//This code is not handling RBAC errors
					_.forEach(errors, (error) => {
						let displayError = utilityfunctions.getErrorFromResponse(error);
						let errorCodeFromApi = utilityfunctions.getErrorCodeFromResponse(displayError);
						let errorMessage = utilityfunctions.getErrorMessageFromResponse(displayError);

						if (!_.isEmpty(errorMessage)) {
							if (errorCodeFromApi === 318) {
								errorMessage = errorMessage + displayError.substr(displayError.indexOf(' -') + 2, displayError.length);
							}
							errorMessages += errorMessage + '\n';
						}
					});

					//This if RBAC error is not handled by this code then allow default logic of showing error using
					//ErrorSummary component.
					if (!_.isEmpty(errorMessages)) {
						setUpdatedOwners({});
					}
				} else {
					setCurrentProject([]);
					dispatch(
						getProjectDetails(projectId, '?includeEngagementsDetail=true&includeGroupDetails=true', {
							supressErrorOnUi: true,
							transform: true
						})
					).then(() => {
						setSearchProjectID(projectId);
						setUpdatedOwners({});
						setIsProjetLoaded(true);
					});
				}
			});

			//set Modal after save

			dispatch(setIsUnsavedUserDataModalOpen(false));
			dispatch(clearUnsavedDataState());
		}
	};

	const revertChanges = () => {
		//revert change for Status
		if (_.size(currentProject) > 0) {
			const value = currentResource.ProjectStatusLabels.find(
				(x) => x.value.toLowerCase() === currentProject[0].status.toLowerCase()
			)?.label;
			document.querySelector('.motif-select-input-text').textContent = value;
			setSelectedStatus(currentProject[0].status.toLowerCase());
			dispatch(setUnsavedDataModalState('projectStatus', false));
			//revert changes for add/remove project owners
			setUpdatedOwners({});
		}

		//revert changes for add members
		setSelectedGroupMembers([]);
		dispatch(setUnsavedDataModalState('addMembers', false));

		//set Modal for revert changes
		dispatch(setIsUnsavedUserDataModalOpen(false));
	};

	const loadGroups = () => {
		let url = `${env.getBaseURL(serviceURL)}Projects/${project.projectId}/Groups`;
		dispatch(getProjectAccessGroups(url))
			.then(() => {})
			.catch(() => {});
	};

	const onAddMemberClick = () => {
		loadGroups();
		setShowAddMemberModal(true);
		setSelectedUser({});
	};

	const hideAddMemberModal = () => {
		setShowAddMemberModal(false);
		setSelectedUser({});
	};

	const saveAddedMembers = () => {
		let promises = [];
		let errors = [];
		setSaveFailed(false);
		_.forEach(selectedGroupMembers, (groupMembers) => {
			let users = [];
			const payloadData = users;
			const url = `${env.getBaseURL(serviceURL)}Projects/${project.projectId}/Groups/${groupMembers.groupId}/Users`;

			_.forEach(groupMembers.users, (user) => {
				users.push({
					userId: user.userId || user.email,
					firstName: user.firstName,
					lastName: user.lastName,
					emailAddress: user.emailAddress,
					isInternal: user.isInternal
				});
			});
			promises.push(
				dispatch(addMember(url, payloadData)).catch((error) => {
					errors.push(error);
				})
			);
		});

		Promise.all(promises).then(() => {
			let errorMessages = '';
			if (_.size(errors) > 0) {
				//This code is not handling RBAC errors
				_.forEach(errors, (error) => {
					let displayError = utility.getErrorFromResponse(error);
					let errorCodeFromApi = utility.getErrorCodeFromResponse(displayError);
					let errorMessage = utility.getErrorMessageFromResponse(displayError);

					if (!_.isEmpty(errorMessage)) {
						if (errorCodeFromApi === 318) {
							let errorDetails = displayError.substr(displayError.indexOf(' -') + 2, displayError.length);

							let userNames = errorDetails.substr(errorDetails.indexOf(':') + 2, errorDetails.length);
							let groupName = errorDetails.substr(0, errorDetails.indexOf(':'));
							errorMessage = stringFormat(errorMessage, userNames, groupName);
						}
						if (errorCodeFromApi === 317) {
							let errorDetails = displayError.substr(displayError.indexOf(' -') + 2, displayError.length);

							let userNames = errorDetails.substr(errorDetails.indexOf(':') + 2, errorDetails.length);
							errorMessage = stringFormat(errorMessage, userNames);
						}
						errorMessages += errorMessage + '</br>';
					}
				});

				//This if RBAC error is not handled by this code then allow default logic of showing error using
				//ErrorSummary component.
				if (!_.isEmpty(errorMessages)) {
					dispatch(clearErrors()).then(() => {
						setToastErrorMessage(errorMessages);
						setSaveFailed(true);
						setSelectedGroupMembers([]);
					});
				}
			} else {
				setSelectedGroupMembers([]);
				setSaveFailed(false);
				setIsProjetLoaded(false);
				dispatch(
					getProjectDetails(project.projectId, '?includeEngagementsDetail=true&includeGroupDetails=true', {
						supressErrorOnUi: true,
						transform: true
					})
				);
			}
		});
	};

	const getAddedMembers = () => {
		if (selectedGroupMembers.length > 0) {
			var addedMembers = [];
			_.forEach(selectedGroupMembers, (group) => {
				_.forEach(group.users, (user) => {
					var existingUserIndex = addedMembers.findIndex(
						(x) => x.userId === user.userId || x.userId === user.emailAddress
					);
					let currentGroupInfo = groupsInfo.filter((x) => x.id === group.groupId)[0];
					if (existingUserIndex > -1) {
						if (currentGroupInfo.isBuiltIn) {
							addedMembers[existingUserIndex].isProjectOwner = true;
						}
						addedMembers[existingUserIndex]?.connectedGroups?.push({
							groupId: currentGroupInfo.accessGroupId,
							groupName: currentGroupInfo.groupName
						});
					} else {
						addedMembers.push({
							connectedGroups: [
								{
									groupId: currentGroupInfo.accessGroupId,
									groupName: currentGroupInfo.groupName
								}
							],
							emailAddress: user.emailAddress,
							firstName: user.firstName,
							initial: user.initial,
							isProjectOwner: groupsInfo.filter((x) => x.isBuiltIn && x.id === group.groupId).length > 0,
							lastName: user.lastName,
							roles: user.roles,
							userId: user.userId || user.emailAddress,
							userType: user.isInternal ? 'Member' : 'Guest'
						});
					}
				});
			});

			return addedMembers;
		} else {
			return [];
		}
	};
	//#endregion

	return (
		<StyledManageProjectsSection>
			{/* <Prompt when={isProjectLoaded} message={(location) => `Are you sure you want to go to ${location.pathname}`} /> */}
			<section className="admin-users-wrapper customScrollbar">
				{(userPreferences.isAdminReader || userPreferences.isAdminWriter || userPreferences.isSuperAdmin) && (
					<>
						<section className="mainResponsiveWrapper">
							<header>
								<h3 className="ellipsis">{utilityfunctions.removeTags(labels.manageProjectsFormatted)}</h3>
							</header>
							<p className="instructional-text ellipsis">{labels.manageProjectsInstructionalText}</p>
							<section className="search-wrap">
								<MotifSearch
									styleName="manage-search"
									value={searchProjectID}
									placeholder={labels.search}
									id={'search-manage-users'}
									searchHoverText={labels.search}
									onChange={(event) => handlerOnSearchChange(event.target.value)}
									onSearchButtonClick={handlerSearch}
									onClear={handlerClearSearch}
									onEnter={() => handlerSearch({searchProjectID})}
								/>
								{errorMessage && errorMessage.length > 0 && <MotifErrorMessage>{errorMessage}</MotifErrorMessage>}
								{showNoResultMessage && <section className="no-records-msg">{labels.noResultMessage}</section>}
							</section>
							<TrackerLoader
								areas={[
									promiseAreas.GET_PROJECT_DETAILS_AREA,
									promiseAreas.USER_PROJECTS_AREA,
									promiseAreas.ADD_MEMBER_TO_GROUP
								]}
								view="__"
								styleName="CopyGroupLoader"
								fullscreen={false}
							/>
							<section className="user-details-wrap">
								<header>
									<h4>{labels.projectDetails}</h4>
								</header>
								<CommonAdminTable config={detailsTableConfig} />
							</section>
							<section className="instructional-btn-wrap">
								<TabBar
									badge
									activeTab={projectsActiveTab}
									onTabChange={(e) => {
										setProjectsActiveTab(e);
									}}
									tabs={managerProjectTabs}
									styleName="manage-projects-tabbar"
								/>
								{project.projectId &&
									projectsActiveTab === manageProjectTabs.projectOwners &&
									userPreferences &&
									(userPreferences.isAdminWriter || userPreferences.isSuperAdmin) && (
										<section className="add-button-wrap">
											{currentProject &&
												currentProject[0]?.status.toLowerCase() !== projectStatusEnum.DRAFT &&
												currentProject[0]?.status.toLowerCase() !== projectStatusEnum.DELETE && (
													<>
														<Button onClick={onAddMemberClick} svg={contentIcAdd24px} title={labels.addProjectOwner} />
														<Button
															onClick={onAddMemberClick}
															//disabled={props.disabled}
															variant={'text-alt'}
															title={labels.addProjectOwner}
															label={labels.addProjectOwner}
														/>
													</>
												)}
										</section>
									)}
							</section>
							{showAddMemberModal && (
								<AddMemberGroup
									projectId={project.projectId}
									userData={selectedUser}
									isEdit={false}
									hideAddMemberModal={hideAddMemberModal}
									userLookupType={UserLookupType.Admin}
									setSelectedGroupMembers={setSelectedGroupMembers}
									selectedGroupMembers={selectedGroupMembers}
									addedMembers={getAddedMembers()}
									isOversightAdmin={userPreferences && (userPreferences.isAdminWriter || userPreferences.isSuperAdmin)}
								/>
							)}
							{renderSwitch(projectsActiveTab)}
						</section>
					</>
				)}
			</section>
			{userPreferences && (userPreferences.isAdminWriter || userPreferences.isSuperAdmin) && (
				<>
					{currentProject &&
						currentProject[0]?.status.toLowerCase() !== projectStatusEnum.DRAFT &&
						currentProject[0]?.status.toLowerCase() !== projectStatusEnum.DELETE && (
							<section className="footer-wrap">
								<Button onClick={() => save()} variant={'primary-alt'} title={labels.save} label={labels.save} />
								<Button
									onClick={() => revertChanges()}
									variant={'secondary'}
									title={labels.revertChanges}
									label={labels.revertChanges}
								/>
							</section>
						)}
				</>
			)}
		</StyledManageProjectsSection>
	);
};

export default ManageProjects;

const StyledManageProjectsSection = styled.section`
	position: relative;
	width: 100%;
	height: 100%;
	.admin-users-wrapper {
		width: 100%;
		height: calc(100% - 11.5rem);
		margin-top: 5rem;
		// margin-bottom: 6rem;
	}

	header {
		width: 100%;
		h3 {
			margin-bottom: 0;
		}

		h4 {
			margin: 0;
			&.delegate-to-header {
				margin-top: 3rem;
			}
		}
	}

	.instructional-btn-wrap {
		display: flex;
		justify-content: space-between;

		.add-button-wrap {
			display: flex;
			align-items: center;
			max-width: 20rem;
			padding: 0;
			margin-top: 2rem;
		}
	}
	.project-name {
		width: 100%;
	}
	.empty-container {
		background: ${(props) => props.theme.color[`whiteblack`]};
		display: flex;
		flex-direction: column;
		flex-grow: 1;
		text-align: center;
		font-size: 1.4rem;
		padding: 1.5rem 0;
		color: ${(props) => props.theme.color[`grey400`]};
	}
	.manage-projects-tabbar {
		padding: 0;
		.motif-tab-bar {
			border-bottom: 0;
			.motif-button {
				max-width: 20rem;
				font-size: 1.4rem;
				display: inline-block;
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
				&:first-child {
					padding-left: 1.5rem;
				}
			}
		}
	}
	.instructional-text {
		font-size: 1.4rem;
	}
	.search-wrap {
		margin-bottom: 3rem;

		.motif-error-message {
			font-size: 1.2rem;
			svg {
				width: 1.5rem;
				height: 1.5rem;
			}
		}

		.no-records-msg {
			font-size: 1.4rem;
			margin-top: 1rem;
		}
	}
	.manage-search {
		width: 20%;
		.motif-input {
			font-size: 1.4rem;
			&::placeholder {
				font-size: 1.4rem;
			}
		}
	}

	.remove-row {
		align-items: center;
		display: flex;
	}

	.tabs-proj-table,
	.project-details-table {
		margin-top: 2rem;

		.list-body {
			div {
				&:last-child {
					.table-row {
						border-bottom: 0;
					}
				}
			}
		}

		.motif-card {
			border: solid 1px ${(props) => props.theme.color[`grey300`]};
		}
		.empty-container,
		.table-row,
		.row-cell-0,
		.row-cell-1,
		.row-cell-2,
		.row-cell-3,
		.row-cell-4,
		.row-cell-5,
		.table-header-item {
			font-size: 1.4rem;
		}
		&.clients-table {
			width: 80%;
		}
	}
	.tabs-proj-table {
		width: 100%;
	}
	.project-details-table {
		width: 80%;
		.list-body {
			overflow-y: unset;
			max-height: 50rem;

			.row-cell-2 {
				overflow: unset;
				.motif-form-field {
					width: 50%;
					.motif-select-trigger {
						height: 3.5rem;
						padding: 0 1rem;
						font-size: 1.4rem;
					}
					.motif-option-text {
						width: 100%;
						font-size: 1.4rem;
					}
					.motif-select-list-options {
						display: flex;
						flex-direction: column;
						.motif-option {
							padding: 0 1rem;
						}
					}
				}
				.manage-admin-dropdown {
					.motif-select-input {
						padding-top: 0;
						min-height: 2rem;
						height: 4rem;
						.motif-select-input-arrow {
							top: 1.3rem;
						}
					}
					.motif-select-wrapper-options {
						.motif-select-options {
							.motif-option {
								display: flex;
							}
						}
					}
				}
			}
		}
	}

	.motif-form-field {
		margin-bottom: 0;
	}

	.manage-projects-tabbar {
		margin-top: 2rem;
	}

	.tabs-proj-table {
		margin-bottom: 3rem;
		.sorting-icons {
			margin-top: 0.5rem;
		}
		.list-body {
			max-height: 50rem;
		}
		a {
			color: ${(props) => props.theme.color[`blue600yellow400`]};
		}
	}

	.user-settings-wrap {
		margin: 3rem 0;
		display: flex;
		font-size: 1.4rem;

		.col-1 {
			width: 30%;
		}
		.col-2 {
			width: 20%;
		}
		.col-3 {
			width: 30%;
		}

		.display-lang-wrap,
		.date-format-wrap,
		.number-format-wrap,
		.time-zone-wrap,
		.theme-wrap,
		.status-wrap {
			display: flex;
			flex-direction: column;
			margin-bottom: 1rem;
			line-height: 2rem;
			max-width: 80%;
		}
		.display-lang-label,
		.date-format-label,
		.number-format-label,
		.time-zone-label,
		.theme-label,
		.status-label {
			font-weight: bold;
			max-width: 100%;
		}
		.display-lang-value,
		.date-format-value,
		.number-format-value,
		.time-zone-value,
		.theme-value,
		.status-value {
			max-width: 100%;
		}

		.label-wrap {
			display: flex;
			align-items: center;
		}
	}

	.footer-wrap {
		border-top: solid 1px ${(props) => props.theme.color[`grey300`]};
		position: absolute;
		bottom: 0;
		width: 100%;
		padding: 1.5rem 2rem 1.5rem 1.8rem;

		button {
			font-size: 1.4rem;
			font-weight: 700 !important;
			padding: 0.5rem 2rem;
			margin-right: 2rem;
			max-width: 20rem;
		}
	}

	.ellipsis {
		display: inline-block;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
`;
